import { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import LauncherContainerWrapper from '../launcher/container/LauncherContainerWrapper';
import { withBrowserSizeContext } from '../containers/withBrowserSizeContext';
import WidgetWindow from './WidgetWindow';
import { FocusScope } from '@react-aria/focus';
import { CANDY_APPLE } from 'visitor-ui-component-library/constants/WidgetColors';
import { AccessibilityContext } from 'conversations-visitor-message-history/accessibility/AccessibilityContext';
import { getUsePillLauncher, getIsUngatedForUpdatedChatLauncherExperience, getIsUngatedForStandaloneRecommendedQuestions } from '../widget-data/selectors/widgetDataSelectors';
import { handleDragHandlePositionChange } from '../post-message/handleDragHandlePositionChange';
import WidgetWelcomePage from './WidgetWelcomePage';
import InitialPageContentWrapper from 'conversations-visitor-experience-components/visitor-widget/components/InitialPageContentWrapper';
import { CustomerAgentHoverProvider, useCustomerAgentHover } from 'conversations-visitor-experience-components/contexts/CustomerAgentHoverContext';
import { getInitialMessageForWelcomePage } from '../selectors/widgetDataSelectors/getInitialMessageForWelcomePage';
import { hasPersistedThreads } from '../threads/selectors/hasPersistedThreads';
import { getRecommendedQuestionsForAgent } from '../widget-data/selectors/getRecommendedQuestionsForAgent';
import { useDetachedLayoutBehavior } from './useDetachedLayoutBehavior';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DetachedWindowError = styled.div.withConfig({
  displayName: "ApplicationLayout__DetachedWindowError"
})(["margin-top:8px;padding:8px 10px;border-radius:8px;color:", ";font-size:12px;"], CANDY_APPLE);
function ApplicationLayoutContent({
  inline,
  isOpen,
  mobile,
  showInitialMessageBubble,
  speechPocActive,
  toggleOpen,
  trackUserInteraction,
  widgetLocation,
  isAIChatBot,
  onSetMessageTextExternally
}) {
  const {
    isDetached,
    isHostingPopup,
    focusDetachedWindow,
    closeDetachedWindow,
    clearDetachedWindowError,
    resolveWidgetOpenWithDetach,
    resolveLauncherHiddenWithDetach,
    detachedWindowError
  } = useDetachedLayoutBehavior();
  const isWidgetOpen = resolveWidgetOpenWithDetach(isOpen);
  const [animationsFinished, setAnimationsFinished] = useState(true);
  const [shouldRenderDialog, setShouldRenderDialog] = useState(isWidgetOpen);
  const accessibilityContext = useContext(AccessibilityContext);
  const usePillLauncher = useSelector(getUsePillLauncher);
  const isUngatedForUpdatedLauncher = useSelector(getIsUngatedForUpdatedChatLauncherExperience);
  const hasExistingThreads = useSelector(hasPersistedThreads);
  const recommendedQuestionsForAgent = useSelector(getRecommendedQuestionsForAgent);
  const isUngatedForStandaloneRecommendedQuestions = useSelector(getIsUngatedForStandaloneRecommendedQuestions);
  const hasPrompts = recommendedQuestionsForAgent.length > 0;
  const {
    isHoverActive: isHoveringPillLauncher,
    resetHoverState
  } = useCustomerAgentHover();
  const shouldHideLauncherForExpandedContainer = usePillLauncher && isUngatedForUpdatedLauncher && isHoveringPillLauncher && !hasExistingThreads && hasPrompts;
  useEffect(() => {
    if (isWidgetOpen) {
      return;
    }
    if (shouldHideLauncherForExpandedContainer) {
      handleDragHandlePositionChange({
        dragHandleState: 'hidden'
      });
      return;
    }
    const dragHandleState = usePillLauncher ? 'pill-launcher' : 'icon-launcher';
    handleDragHandlePositionChange({
      dragHandleState
    });
  }, [usePillLauncher, shouldHideLauncherForExpandedContainer, isWidgetOpen]);
  const openWidget = () => {
    if (isDetached) {
      focusDetachedWindow();
      return;
    }
    clearDetachedWindowError();
    setAnimationsFinished(false);
    trackUserInteraction();
    toggleOpen({
      isOpened: true,
      isUser: true,
      openedFrom: usePillLauncher ? 'pill launcher' : 'icon launcher'
    });
    accessibilityContext.setShouldTrapFocus(true);
  };
  const closeWidget = () => {
    if (isDetached) {
      closeDetachedWindow();
      return;
    }
    setAnimationsFinished(false);
    resetHoverState();
    trackUserInteraction();
    toggleOpen({
      isOpened: false,
      isUser: true
    });
  };
  const closeWidgetWelcomePage = () => {
    setAnimationsFinished(true);
    trackUserInteraction();
  };
  const onOpenAnimationStarted = () => {
    setAnimationsFinished(false);
    setShouldRenderDialog(true);
    if (usePillLauncher) {
      handleDragHandlePositionChange({
        dragHandleState: 'hidden'
      });
    }
  };
  const onOpenAnimationFinished = () => {
    setAnimationsFinished(true);
    accessibilityContext.setShouldTrapFocus(true);
    handleDragHandlePositionChange({
      dragHandleState: 'widget-open'
    });
  };
  const onCloseAnimationStarted = () => {
    setAnimationsFinished(false);
    if (usePillLauncher) {
      handleDragHandlePositionChange({
        dragHandleState: 'hidden'
      });
    }
  };
  const onCloseAnimationFinished = () => {
    setAnimationsFinished(true);
    setShouldRenderDialog(false);
    accessibilityContext.setShouldTrapFocus(false);
    handleDragHandlePositionChange({
      dragHandleState: usePillLauncher ? 'pill-launcher' : 'icon-launcher'
    });
  };
  const onLauncherAnimationStart = () => {
    handleDragHandlePositionChange({
      dragHandleState: 'hidden'
    });
  };
  const onLauncherAnimationFinished = () => {
    handleDragHandlePositionChange({
      dragHandleState: isHoveringPillLauncher ? 'hidden' : 'pill-launcher'
    });
  };
  const baseLauncherHidden = isOpen && mobile || inline || isOpen && usePillLauncher;
  const isLauncherHidden = resolveLauncherHiddenWithDetach(baseLauncherHidden);
  const showContent = !usePillLauncher || usePillLauncher && animationsFinished;
  const initialMessage = useSelector(getInitialMessageForWelcomePage);
  return /*#__PURE__*/_jsx(AccessibilityContext.Consumer, {
    children: ({
      shouldTrapFocus,
      isMessageEditorFocused
    }) => /*#__PURE__*/_jsxs(FocusScope, {
      contain: shouldTrapFocus && !isMessageEditorFocused && !isHostingPopup,
      autoFocus: false,
      children: [/*#__PURE__*/_jsx(WidgetWindow, {
        inline: inline,
        isOpen: !isHostingPopup && (isWidgetOpen || inline),
        shouldRenderDialog: shouldRenderDialog,
        mobile: mobile,
        widgetLocation: widgetLocation,
        onOpenAnimationStarted: onOpenAnimationStarted,
        onCloseAnimationFinished: onCloseAnimationFinished,
        onOpenAnimationFinished: onOpenAnimationFinished,
        onCloseAnimationStarted: onCloseAnimationStarted,
        onClose: closeWidget,
        speechPocActive: speechPocActive,
        usePillLauncher: usePillLauncher,
        onSetMessageTextExternally: onSetMessageTextExternally
      }), showContent && !inline && (!isWidgetOpen || !usePillLauncher || isHostingPopup) && /*#__PURE__*/_jsxs(InitialPageContentWrapper, {
        isMobile: mobile,
        widgetLocation: widgetLocation,
        usePillLauncher: usePillLauncher,
        shouldTrapFocus: !isDetached && isAIChatBot && usePillLauncher && !mobile,
        isUngatedForLauncherExperience: isUngatedForUpdatedLauncher,
        children: [showInitialMessageBubble && !isDetached && !isWidgetOpen && animationsFinished && /*#__PURE__*/_jsx(WidgetWelcomePage, {
          onClose: closeWidgetWelcomePage,
          initialMessage: initialMessage,
          isMobile: mobile
        }), !isLauncherHidden && !shouldHideLauncherForExpandedContainer && /*#__PURE__*/_jsx(LauncherContainerWrapper, {
          onClose: closeWidget,
          onOpen: openWidget,
          usePillLauncher: usePillLauncher,
          onLauncherAnimationStart: onLauncherAnimationStart,
          onLauncherAnimationFinished: onLauncherAnimationFinished,
          disableHoverExpansion: isUngatedForUpdatedLauncher && (hasExistingThreads || !hasPrompts || isUngatedForStandaloneRecommendedQuestions)
        }), detachedWindowError && /*#__PURE__*/_jsx(DetachedWindowError, {
          "data-test-id": "detached-window-error",
          children: detachedWindowError
        })]
      })]
    })
  });
}
ApplicationLayoutContent.displayName = 'ApplicationLayoutContent';
export function ApplicationLayout(props) {
  const usePillLauncher = useSelector(getUsePillLauncher);
  const isUngatedForStandaloneRecommendedQuestions = useSelector(getIsUngatedForStandaloneRecommendedQuestions);
  return /*#__PURE__*/_jsx(CustomerAgentHoverProvider, {
    enableHoverTracking: usePillLauncher && !isUngatedForStandaloneRecommendedQuestions,
    isUngatedForLauncherExperience: props.isUngatedForLauncherExperience,
    children: /*#__PURE__*/_jsx(ApplicationLayoutContent, Object.assign({}, props))
  });
}
ApplicationLayout.displayName = 'ApplicationLayout';
export default withBrowserSizeContext(ApplicationLayout);